import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Lesson, Attachment } from '../types';

// كاش للدروس
interface LessonCacheItem {
  data: Lesson;
  timestamp: number;
}

const lessonCache: Record<string, LessonCacheItem> = {};
const LESSON_CACHE_EXPIRY = 5 * 60 * 1000; // 5 دقائق

// دالة للحصول على درس مع التخزين المؤقت
export const getLesson = async (lessonId: string): Promise<Lesson | null> => {
  try {
    const now = Date.now();
    
    // التحقق من وجود البيانات في الكاش وما إذا كانت صالحة
    if (lessonCache[lessonId] && now - lessonCache[lessonId].timestamp < LESSON_CACHE_EXPIRY) {
      console.log(`استخدام بيانات الدرس المخزنة مؤقتًا للدرس: ${lessonId}`);
      return lessonCache[lessonId].data;
    }
    
    const lessonDoc = await getDoc(doc(db, 'lessons', lessonId));
    
    if (lessonDoc.exists()) {
      const lessonData = {
        id: lessonDoc.id,
        ...lessonDoc.data()
      } as Lesson;
      
      // تخزين البيانات في الكاش
      lessonCache[lessonId] = {
        data: lessonData,
        timestamp: now
      };
      
      return lessonData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting lesson:', error);
    throw error;
  }
};

// دالة لإلغاء صلاحية كاش الدرس عند تحديث أو حذف درس
export const invalidateLessonCache = (lessonId: string) => {
  if (lessonCache[lessonId]) {
    delete lessonCache[lessonId];
  }
};

// Get all lessons for a course
export const getLessonsByCourseId = async (courseId: string) => {
  try {
    const lessonsQuery = query(
      collection(db, 'lessons'),
      where('courseId', '==', courseId),
      orderBy('order', 'asc')
    );
    
    const querySnapshot = await getDocs(lessonsQuery);
    const lessons: Lesson[] = [];
    
    querySnapshot.forEach((doc) => {
      lessons.push({ id: doc.id, ...doc.data() } as Lesson);
    });
    
    return lessons;
  } catch (error) {
    console.error('Error getting lessons:', error);
    throw error;
  }
};

// Get lesson by ID
export const getLessonById = async (id: string) => {
  try {
    const lesson = await getLesson(id);
    
    if (!lesson) {
      throw new Error('Lesson not found');
    }
    
    return lesson;
  } catch (error) {
    console.error('Error getting lesson:', error);
    throw error;
  }
};

// Create a new lesson
export const createLesson = async (
  lessonData: Omit<Lesson, 'id' | 'videoUrl' | 'createdAt' | 'updatedAt'>, 
  videoFile: File,
  attachmentFiles?: File[]
) => {
  try {
    // Upload video
    const videoStorageRef = ref(storage, `lessonVideos/${Date.now()}_${videoFile.name}`);
    const videoUploadTask = await uploadBytesResumable(videoStorageRef, videoFile);
    const videoUrl = await getDownloadURL(videoUploadTask.ref);
    
    // Upload attachments if provided
    const attachments: Attachment[] = [];
    
    if (attachmentFiles && attachmentFiles.length > 0) {
      for (const file of attachmentFiles) {
        const attachmentRef = ref(storage, `lessonAttachments/${Date.now()}_${file.name}`);
        const uploadTask = await uploadBytesResumable(attachmentRef, file);
        const url = await getDownloadURL(uploadTask.ref);
        
        attachments.push({
          id: Date.now().toString(),
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          createdAt: Date.now()
        });
      }
    }
    
    const lessonRef = await addDoc(collection(db, 'lessons'), {
      ...lessonData,
      videoUrl,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    
    return { 
      id: lessonRef.id, 
      ...lessonData, 
      videoUrl, 
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: Date.now(), 
      updatedAt: Date.now() 
    } as Lesson;
  } catch (error) {
    console.error('Error creating lesson:', error);
    throw error;
  }
};

// Update an existing lesson
export const updateLesson = async (
  id: string, 
  lessonData: Partial<Lesson>, 
  videoFile?: File,
  attachmentFiles?: File[],
  attachmentsToDelete?: string[]
) => {
  try {
    const lessonRef = doc(db, 'lessons', id);
    const lessonDoc = await getDoc(lessonRef);
    
    if (!lessonDoc.exists()) {
      throw new Error('Lesson not found');
    }
    
    const currentLesson = { id, ...lessonDoc.data() } as Lesson;
    let videoUrl = currentLesson.videoUrl;
    let attachments = currentLesson.attachments || [];
    
    // Upload new video if provided
    if (videoFile) {
      // Delete old video
      try {
        const oldVideoRef = ref(storage, currentLesson.videoUrl);
        await deleteObject(oldVideoRef);
      } catch (error) {
        console.warn('Error deleting old video:', error);
      }
      
      const videoStorageRef = ref(storage, `lessonVideos/${Date.now()}_${videoFile.name}`);
      const videoUploadTask = await uploadBytesResumable(videoStorageRef, videoFile);
      videoUrl = await getDownloadURL(videoUploadTask.ref);
    }
    
    // Delete attachments if specified
    if (attachmentsToDelete && attachmentsToDelete.length > 0) {
      for (const attachmentId of attachmentsToDelete) {
        const attachment = attachments.find((a: Attachment) => a.id === attachmentId);
        
        if (attachment) {
          try {
            const attachmentRef = ref(storage, attachment.url);
            await deleteObject(attachmentRef);
          } catch (error) {
            console.warn('Error deleting attachment:', error);
          }
          
          attachments = attachments.filter((a: Attachment) => a.id !== attachmentId);
        }
      }
    }
    
    // Upload new attachments if provided
    if (attachmentFiles && attachmentFiles.length > 0) {
      for (const file of attachmentFiles) {
        const attachmentRef = ref(storage, `lessonAttachments/${Date.now()}_${file.name}`);
        const uploadTask = await uploadBytesResumable(attachmentRef, file);
        const url = await getDownloadURL(uploadTask.ref);
        
        attachments.push({
          id: Date.now().toString(),
          name: file.name,
          url,
          type: file.type,
          size: file.size,
          createdAt: Date.now()
        });
      }
    }
    
    await updateDoc(lessonRef, {
      ...lessonData,
      videoUrl,
      attachments: attachments.length > 0 ? attachments : [],
      updatedAt: Date.now()
    });
    
    // استخراج id من currentLesson وإنشاء كائن جديد بدون id
    const { id: _, ...currentLessonWithoutId } = currentLesson;
    
    // إلغاء صلاحية الكاش
    invalidateLessonCache(id);
    
    return { 
      id, 
      ...currentLessonWithoutId,
      ...lessonData, 
      videoUrl, 
      attachments,
      updatedAt: Date.now() 
    } as Lesson;
  } catch (error) {
    console.error('Error updating lesson:', error);
    throw error;
  }
};

// Delete a lesson
export const deleteLesson = async (id: string) => {
  try {
    const lessonRef = doc(db, 'lessons', id);
    const lessonDoc = await getDoc(lessonRef);
    
    if (!lessonDoc.exists()) {
      throw new Error('Lesson not found');
    }
    
    const lesson = { id, ...lessonDoc.data() } as Lesson;
    
    // Delete video
    try {
      const videoRef = ref(storage, lesson.videoUrl);
      await deleteObject(videoRef);
    } catch (error) {
      console.warn('Error deleting lesson video:', error);
    }
    
    // Delete attachments
    if (lesson.attachments && lesson.attachments.length > 0) {
      for (const attachment of lesson.attachments) {
        try {
          const attachmentRef = ref(storage, attachment.url);
          await deleteObject(attachmentRef);
        } catch (error) {
          console.warn('Error deleting attachment:', error);
        }
      }
    }
    
    await deleteDoc(lessonRef);
    
    // إلغاء صلاحية الكاش
    invalidateLessonCache(id);
    
    return true;
  } catch (error) {
    console.error('Error deleting lesson:', error);
    throw error;
  }
};
